import { defineComponent, ref, computed, watch, onMounted, inject } from 'vue';
import SHeaderSection from '@/components/stemdo-components/s-header-section/SHeaderSection.vue';
import SButtonBack from '@/components/stemdo-components/s-button-back/SButtonBack.vue';
import { IStemdoer, Stemdoers } from '@/shared/model/stemdoer.model';
import CheckoutOverview from './checkout-overview/CheckoutOverview.vue';
import { useStore } from '@/plugins/vuex';
import { useRouter } from '@/plugins/router';
import { CardOptions } from '@/components/stemdo-components/s-card-stemdoer/sCardStemdoer';
import { TeamRequest } from '@/shared/model/team-request.model';
import { Projects } from '@/shared/model/project.model';
import ProjectService from '@/sections/admin/entities/project/project.service';
import SModalTeamRequestConfirmation from '@/components/stemdo-components/s-modal-team-request-confirmation/SModalTeamRequestConfirmation.vue';
import ButtonsGroup from './buttons-group/ButtonsGroup.vue';
import { ExtendedTeamRequestStatus } from './enum/extendedTeamRequestStatus';
import { RequestStatus } from '@/shared/model/enumerations/request-status.model';
import TeamRequestService from '@/sections/admin/entities/team-request/team-request.service';
import PdfService from '@/sections/admin/entities/pdf/pdf.service';
import ClientService from '@/sections/admin/entities/client/client.service';
import ModalStemdoerDetail from '../stemdoer-detail/ModalStemdoerDetail.vue';
import SRadarStemdoersTechs from '@/components/stemdo-components/s-radar-stemdoers-techs/SRadarStemdoersTechs.vue';
import SFootprint from '@/components/stemdo-components/s-footprint/SFootprint.vue';
import CompanyService from '@/sections/admin/entities/company/company.service';
import { IClient } from '@/shared/model/client.model';
import { IAssociatedClient } from '@/shared/model/associated-client.model';

export default defineComponent({
  props: {
    teamRequestId: {
      type: String
    }
  },
  components: {
    SButtonBack,
    CheckoutOverview,
    SHeaderSection,
    SModalTeamRequestConfirmation,
    ButtonsGroup,
    SRadarStemdoersTechs,
    SFootprint,
    ModalStemdoerDetail
  },
  setup(props) {
    const store = useStore();
    const router = useRouter();
    const teamRequestService: TeamRequestService = inject('teamRequestService');
    const projectService: ProjectService = inject('projectService');
    const projects = ref<{ id: string; name: string }[]>([]);
    const pdfService: PdfService = inject('pdfService');
    const companyService = inject('companyService') as CompanyService;
    const clientService = inject('clientService') as () => ClientService;
    const isUserManager = computed(() => store.getters.account?.authorities.includes('ROLE_GESTOR') ?? false);
    const isUserClient = computed(() => store.getters.account?.authorities.includes('ROLE_CLIENT') ?? false);
    const teamRequest = ref<TeamRequest>(TeamRequest.empty());
    const clientSelected = computed(() => store.getters['cartStore/clientSelected']);
    const companySelected = computed(() => store.getters['cartStore/companySelected']);
    const hourlyCost = ref<number>(0);
    const isStemdoerDetailModalOpen = ref(false);
    const selectedStemdoer = ref<IStemdoer>({});
    const selectedStemdoerRate = ref<number>(0);
    const stemdoerList = ref<IStemdoer[]>([]);
    const pdfLoading = ref(false);
    const extendedTeamRequestStatus = computed(() => {
      if (isUserManager.value) {
        if (!props.teamRequestId) {
          return ExtendedTeamRequestStatus.MANAGER_AND_CHECKOUT;
        }
        if (
          props.teamRequestId &&
          (teamRequest.value.status === RequestStatus.PENDING || teamRequest.value.status === RequestStatus.VALIDATED)
        ) {
          return ExtendedTeamRequestStatus.MANAGER_AND_NEW_PROPOSAL;
        }
      }
      if (isUserClient.value) {
        if (props.teamRequestId) {
          if (teamRequest.value.status === RequestStatus.VALIDATED) {
            return ExtendedTeamRequestStatus.CLIENT_AND_VALIDATED;
          }
          if (teamRequest.value.status === RequestStatus.SAVED) {
            return ExtendedTeamRequestStatus.CLIENT_AND_SAVED;
          }
        } else {
          return ExtendedTeamRequestStatus.CLIENT_AND_CHECKOUT;
        }
      }
      return undefined;
    });
    const isPdfButtonDisabled = computed(() => {
      if (isUserManager.value) {
        return !clientSelected.value;
      }
      return pdfLoading.value;
    });

    // Método para obtener proyectos filtrados por el cif de la company seleccionada
    const getProjectsByCompanyCif = async () => {
      if (companySelected.value && companySelected.value.cif) {
        try {
          const response = await projectService.retrieveAllByCompanyCif(companySelected.value.cif);
          const filtered = Array.isArray(response.data) ? response.data.map((p: any) => ({ id: p.id, name: p.name })) : [];
          projects.value = filtered;
        } catch (error) {
          console.error('Error obtaining projects by company CIF:', error);
          projects.value = [];
        }
      } else {
        projects.value = [];
      }
    };

    // Inicializa los rates del checkoutStore y obtiene los projects al montar la vista
    onMounted(async () => {
      await getProjectsByCompanyCif();
      store.dispatch('checkoutStore/initStemdoerRatesFromCart');
      if (props.teamRequestId) {
        teamRequest.value = await retrieveTeamRequest(props.teamRequestId);
        teamRequest.value.removeNotAvailableStemdoers();
      }

      if (isUserClient.value) {
        const clientId = store.getters['clientAccount'].id;
        clientService()
          .find(clientId)
          .then(res => {
            store.commit('cartStore/setClient', res);
          });
      }

      const stemdoersInCart: Stemdoers = Stemdoers.from(store.getters['cartStore/cartItems']);

      const companyRates = companySelected.value?.rates || [];
      stemdoersInCart.setRates(companyRates);
      if (!props.teamRequestId && isUserClient.value) {
        teamRequest.value.client = { id: clientSelected.value.id, email: clientSelected.value.email };
      }

      teamRequest.value.addStemdoerRates(stemdoersInCart);

      if (teamRequest.value.status === RequestStatus.SAVED) {
        const companyRates = await companyService.retrieveCompanyRates();
        teamRequest.value.stemdoerRates.setRates(companyRates);
      }
      if (isUserClient.value) {
        const companyRates = await companyService.retrieveCompanyRates();
        teamRequest.value.stemdoerRates.setRates(companyRates);
      }

      redirectIfNoStemdoers();
    });

    const checkoutStemdoerRates = computed(() => store.state.checkoutStore.stemdoerRates);

    const getStemdoerRate = (stemdoerId: string, fallbackRate?: number) => {
      const item = checkoutStemdoerRates.value.find(r => r.stemdoerId === stemdoerId);
      if (item && typeof item.rate === 'number') return item.rate;
      return typeof fallbackRate === 'number' ? fallbackRate : 0;
    };

    watch(
      () => teamRequest.value,
      _ => {
        redirectIfNoStemdoers();
        stemdoerList.value = teamRequest.value.stemdoerRates.map(stemdoerRate => stemdoerRate.stemdoer);
        hourlyCost.value = teamRequest.value.calculateTotalRate();
      },
      { deep: true }
    );

    const redirectIfNoStemdoers = () => {
      if (isUserManager.value === true) {
        if (teamRequest.value.stemdoerRates.length <= 0) {
          router.push({ name: 'exploreManager' });
        }
      }
      if (isUserClient.value === true) {
        if (teamRequest.value.stemdoerRates.length <= 0) {
          router.push({ name: 'exploreClient' });
        }
      }
    };

    const retrieveTeamRequest = async (id: string): Promise<TeamRequest> => {
      try {
        const tr = await teamRequestService.find(id);
        return new TeamRequest(tr);
      } catch (error) {
        console.error(error);
      }
    };

    const stemdoerCardOptions = computed(() => ({
      isRateEditable: isUserManager.value,
      isDeletable: true,
      isClickable: true,
      isRateVisible: isUserClient.value,
      projects: projects.value.map(p => ({ id: p.id, desc: p.name })),
      onRateChange: (stemdoerId: string, newRate: number) => {
        store.dispatch('checkoutStore/updateStemdoerRate', { stemdoerId, rate: newRate });
        teamRequest.value.updateRate(stemdoerId, newRate);
      },
      onDelete: id => {
        try {
          store.commit('cartStore/removeStemdoer', id);
          teamRequest.value.removeStemdoerById(id);
        } catch (error) {
          console.error(`Error deleting Stemdoer with id: ${id}`, error);
        }
      },
      onClick: (stemdoerId: string) => {
        const stemdoerRate = teamRequest.value.stemdoerRates.findByStemdoerId(stemdoerId);
        selectedStemdoer.value = stemdoerRate.stemdoer;
        selectedStemdoerRate.value = stemdoerRate.rate;
        isStemdoerDetailModalOpen.value = true;
      },
      onProjectChange: (stemdoerId: string, project: any) => {
        store.dispatch('checkoutStore/updateProject', { stemdoerId, project });
        teamRequest.value.updateProject(stemdoerId, project);
      }
    }));

    const closeStemdoerDetailModal = () => {
      isStemdoerDetailModalOpen.value = false;
    };

    const goToExplore = async () => {
      try {
        //tODO: check if trid exist or manager or client
        if (!props.teamRequestId) await router.back();
        if (isUserManager.value === true) {
          await router.push({ name: 'exploreManager', query: { tr: props.teamRequestId, 'not-load': 'true' } });
        }
        if (isUserClient.value === true) {
          await router.push({ name: 'exploreClient', query: { tr: props.teamRequestId, 'not-load': 'true' } });
        }
      } catch (error) {
        console.error('Error al navegar hacia atrás:', error);
      }
    };

    const downloadPDF = () => {
      pdfLoading.value = true;
      const stemdoerIds = teamRequest.value.stemdoerRates.map(sr => sr.stemdoer.id);
      const stemdoerRates = teamRequest.value.stemdoerRates.map(sr => ({
        stemdoId: sr.stemdoer.stemdoId,
        rate: sr.rate
      }));
      if (isUserManager.value) {
        if (!clientSelected.value) {
          pdfLoading.value = false;
          return;
        }
        const clientEmail: string = clientSelected.value.email;
        pdfService.downloadTeamRequestPdfWithRates(stemdoerRates, clientEmail).then(blobPdf => {
          pdfService.handlePdfDownload(`team-request.pdf`, blobPdf);
          pdfLoading.value = false;
        });
      } else {
        pdfService.downloadTeamRequestPdfFromStemdoers(stemdoerIds).then(blobPdf => {
          pdfService.handlePdfDownload(`team-request.pdf`, blobPdf);
          pdfLoading.value = false;
        });
      }
    };

    return {
      isUserClient,
      teamRequest,
      extendedTeamRequestStatus,
      clientSelected,
      companySelected,
      hourlyCost,
      checkoutStemdoerRates,
      stemdoerCardOptions,
      isStemdoerDetailModalOpen,
      selectedStemdoer,
      stemdoerList,
      selectedStemdoerRate,
      pdfLoading,
      isPdfButtonDisabled,
      projects,
      getStemdoerRate,
      closeStemdoerDetailModal,
      goToExplore,
      downloadPDF
    };
  }
});
